import React, { useEffect, useState } from 'react';
import { Activity, Cpu, Globe2, Zap } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type Stats = {
  gpus_available: number;
  total_vram_gb: number;
  total_vram_tb: number;
  total_tflops: number;
  total_pflops: number;
  pool_types: number;
  regions: string[];
  last_updated: number;
  refresh_interval_seconds: number;
};

// Smoothly animates a numeric value to a new target over ~600ms — used
// so the headline numbers tick up/down visibly when the poll lands
// instead of jumping abruptly. Pure presentation; the underlying API is
// still the source of truth.
function useAnimatedNumber(target: number) {
  const [display, setDisplay] = useState(target);
  useEffect(() => {
    let raf = 0;
    const start = display;
    const startTs = performance.now();
    const duration = 600;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return display;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

export default function ComputeCapacityCard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch('/api/training/compute-stats');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as Stats;
        if (!cancelled) {
          setStats(j);
          setErrored(false);
        }
      } catch {
        if (!cancelled) setErrored(true);
      }
    };
    void tick();
    const interval = window.setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const gpus = useAnimatedNumber(stats?.gpus_available ?? 0);
  const vramTb = useAnimatedNumber(Math.round((stats?.total_vram_tb ?? 0) * 10));
  const pflops = useAnimatedNumber(Math.round((stats?.total_pflops ?? 0) * 10));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe2 className="h-5 w-5 text-canada-red" />
          CanXP Sovereign Compute Network
          {!errored && stats && (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Live
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {errored
            ? 'Network stats temporarily unavailable.'
            : stats
              ? `${stats.pool_types} GPU pools available across ${stats.regions.length} sovereign regions · refreshes every ${stats.refresh_interval_seconds}s`
              : 'Loading network capacity…'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-md border bg-canada-red/[0.03] p-4 text-center">
            <Cpu className="h-4 w-4 mx-auto mb-1 text-canada-red" />
            <div className="text-3xl font-bold tabular-nums">{formatInt(gpus)}</div>
            <div className="text-xs text-muted-foreground mt-1">GPUs ready</div>
          </div>
          <div className="rounded-md border bg-canada-red/[0.03] p-4 text-center">
            <Activity className="h-4 w-4 mx-auto mb-1 text-canada-red" />
            <div className="text-3xl font-bold tabular-nums">
              {(vramTb / 10).toFixed(1)}
              <span className="text-base font-normal text-muted-foreground ml-1">TB</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">total VRAM</div>
          </div>
          <div className="rounded-md border bg-canada-red/[0.03] p-4 text-center">
            <Zap className="h-4 w-4 mx-auto mb-1 text-canada-red" />
            <div className="text-3xl font-bold tabular-nums">
              {(pflops / 10).toFixed(1)}
              <span className="text-base font-normal text-muted-foreground ml-1">PFLOPS</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">peak compute</div>
          </div>
        </div>
        {stats && (
          <p className="text-[11px] text-muted-foreground mt-3 text-center">
            Regions: {stats.regions.join(' · ')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
